import { useMemo } from 'react'
import dayjs from 'dayjs'
import DOMPurify from 'dompurify'
import { Button, Modal, Tag, Tooltip } from 'antd'
import { ClockCircleOutlined, ExportOutlined, FileTextOutlined, PrinterOutlined } from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { HELP_CENTRE_URL } from './mockData'
import type { HelpArticle } from './types'

/**
 * A Help Centre article, read without leaving the app.
 *
 * A dialog rather than another view inside the panel: the panel is 420px wide
 * and an article is a document.
 */

/**
 * An allow-list, not a block-list. Bodies come from our own Intercom workspace,
 * which makes them well-formed but not trusted — a compromise there would
 * otherwise be stored XSS on the same origin as a logged-in session.
 *
 * `iframe` is excluded deliberately. `img` is allowed but pinned to one host
 * below. See PREPPY-HELP-SPEC.md §6.1 before widening either.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4',
  'ul', 'ol', 'li',
  'b', 'strong', 'i', 'em', 'u', 'code', 'pre',
  'blockquote', 'a', 'img',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
]

// `width`/`height` let the browser reserve the box before the image loads; the
// stylesheet controls the rendered size.
const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'src', 'alt', 'width', 'height']

/**
 * Images load from Intercom's CDN or not at all.
 *
 * Unrestricted `img` in an authenticated app is a request to an arbitrary third
 * party on every article open, carrying a referer and confirming a read.
 */
const ALLOWED_IMAGE_HOST = 'downloads.intercomcdn.com'

/**
 * A private instance. `addHook` mutates a registry global to the default export,
 * which jspdf also reaches for — hooks there would strip images and rewrite
 * links in every other caller's sanitising too.
 */
const purifier = DOMPurify(window)

purifier.addHook('afterSanitizeAttributes', (node) => {
  if (!(node instanceof HTMLImageElement)) return
  const src = node.getAttribute('src') ?? ''
  let host = ''
  try {
    host = new URL(src, window.location.origin).hostname
  } catch {
    host = ''
  }
  // Removed rather than left to fail, so a blocked image is absent instead of a
  // broken-image icon where a screenshot should be.
  if (host !== ALLOWED_IMAGE_HOST) node.remove()
})

/**
 * Bodies mostly link to other help articles, and following one in the same tab
 * navigates away from AdvicePrep — losing an unsaved agenda or a half-written
 * ticket.
 */
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node instanceof HTMLAnchorElement && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

function sanitise(html: string): string {
  return purifier.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    /**
     * Both default to true and are tested BEFORE `ALLOWED_ATTR`, so without them
     * every `data-*` survives — and `data-tour` is the walkthrough's anchor
     * contract, so a body could plant a decoy for the spotlight.
     */
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  })
}

export function ArticleReader({
  article,
  position,
  articleCount,
  onClose,
}: {
  article: HelpArticle | null
  /** 1-based, from the rendered list, so it agrees with the server's ordering. */
  position: number
  articleCount: number
  onClose: () => void
}) {
  // Sanitising is not free, and this re-renders on every parent state change.
  const clean = useMemo(() => (article ? sanitise(article.bodyHtml) : ''), [article])

  const updated = article && dayjs(article.updatedAt).isValid()
    ? dayjs(article.updatedAt).format('MMMM YYYY')
    : ''

  return (
    <Modal
      open={Boolean(article)}
      onCancel={onClose}
      footer={null}
      width={820}
      centered={false}
      style={{ top: 40 }}
      // Scrolls internally so the header — and Close — stay put.
      styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', paddingTop: 8 } }}
      title={
        article && (
          <div className="flex flex-wrap items-center gap-2 pr-8">
            <Tag color="blue" className="m-0">
              {article.category}
            </Tag>
            <span className="text-[12.5px] font-semibold text-gray-600">Help Centre</span>
            <div className="ml-auto flex items-center gap-2">
              <Tooltip title="Opens your browser's print dialogue — choose Save as PDF as the destination">
                <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>
                  Save as PDF
                </Button>
              </Tooltip>
              <Tooltip title="Open on the Help Centre — the version to send a colleague">
                <Button
                  size="small"
                  type="text"
                  icon={<ExportOutlined />}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open on the Help Centre"
                />
              </Tooltip>
            </div>
          </div>
        )
      }
    >
      {article && (
        <FeatureThemeProvider featureKey="advisoryIntel">
          <article className="preppy-article">
            <h1 className="font-display text-[30px] font-bold leading-tight tracking-tight text-gray-900">
              {article.title}
            </h1>
            {article.description && (
              <div className="mt-1 text-[15px] text-gray-500">{article.description}</div>
            )}

            <div className="mt-3 flex flex-wrap gap-3.5 border-b border-gray-200 pb-4 text-[11.5px] text-gray-400">
              <span className="inline-flex items-center gap-1">
                <FileTextOutlined /> {article.minutes} read
              </span>
              {updated && (
                <span className="inline-flex items-center gap-1">
                  <ClockCircleOutlined /> Updated {updated}
                </span>
              )}
              {position > 0 && articleCount > 0 && (
                <span>
                  Article {position} of {articleCount}
                </span>
              )}
            </div>

            {/* `clean`, never `article.bodyHtml`. Changing that is a security
                bug, not a simplification. */}
            <div
              className="preppy-article__body"
              dangerouslySetInnerHTML={{ __html: clean }}
            />

            <p className="mt-8 border-t border-gray-200 pt-4 text-[11.5px] text-gray-400">
              AdvicePrep Help Centre ·{' '}
              <a
                href={HELP_CENTRE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="feature-soft-text no-underline"
              >
                Browse all articles
              </a>
            </p>
          </article>
        </FeatureThemeProvider>
      )}
    </Modal>
  )
}
